import axios from "axios";
import _isString from "lodash/isString";
import { jwtDecode } from "jwt-decode";

import { API_TYPES, getBaseURL } from "./apiTypes";
import { redirectToSSOPortal } from "../utils/sso";
import { encodeContent } from "../utils/base64Encoding";

const DEFAULT_TIMEOUT_MS = 30000;
const DEFAULT_HEADERS = {
  "Content-Type": "application/json",
};

export const getSSOToken = () => {
  return localStorage.getItem("sso_token");
};

export function getUserRole() {
  const token = getSSOToken();
  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    return decoded["custom:userRole"] || null;
  } catch (error) {
    console.error("Failed to decode token", error);
    return null;
  }
}

export const isTenantAdmin = () => getUserRole() === "TenantAdmin";

const _api = axios.create({
  headers: DEFAULT_HEADERS,
});

const _isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    console.error("Error decoding token:", error);
    return true;
  }
};

const _handleExpiredToken = () => {
  localStorage.removeItem("sso_token");
  localStorage.removeItem("organization");
  redirectToSSOPortal();
};

const _addAuthorizationInterceptor = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      if (config.skipAuth) {
        return config;
      }
      const token = getSSOToken();

      if (_isTokenExpired(token)) {
        console.warn("Token is expired. Redirecting to login...");
        _handleExpiredToken();
        return Promise.reject(new Error("Token expired"));
      }

      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
};
_addAuthorizationInterceptor(_api);

export const _apiResponse = async ({
  endpoint,
  method = "GET",
  data = {},
  params = null,
  extraHeaders = {},
  responseType = "json",
  skipAuth = false,
  apiType = API_TYPES.DEFAULT,
  timeout = DEFAULT_TIMEOUT_MS,
}) => {
  const organization = localStorage.getItem("organization");
  const baseURL = getBaseURL(organization, apiType);
  const mergedHeaders = {
    ...DEFAULT_HEADERS,
    ...extraHeaders,
    "Content-Type":
      extraHeaders["Content-Type"] || DEFAULT_HEADERS["Content-Type"],
  };

  try {
    return await _api.request({
      url: endpoint,
      method: method,
      baseURL: baseURL,
      data: data,
      params: params,
      headers: mergedHeaders,
      responseType: responseType,
      skipAuth,
      timeout: timeout,
    });
  } catch (error) {
    console.error(`API Request Error for ${baseURL}${endpoint} --> `, error);
    throw error;
  }
};

export const _apiResponseData = async ({
  endpoint,
  method = "GET",
  data = {},
  params = null,
  extraHeaders = {},
  responseType = "json",
  skipAuth = false,
  apiType = API_TYPES.DEFAULT,
  timeout = DEFAULT_TIMEOUT_MS,
}) => {
  const apiResponse = await _apiResponse({
    endpoint: endpoint,
    method: method,
    data: data,
    params: params,
    extraHeaders: extraHeaders,
    responseType: responseType,
    skipAuth: skipAuth,
    apiType: apiType,
    timeout: timeout,
  });
  return apiResponse?.data;
};

// AUTHENTICATION APIs

export const getPublicEncryptionKeyAPI = async () => {
  const response = await _api.request({
    url: `/login/public-key`,
    method: "GET",
    baseURL: getBaseURL(organization, API_TYPES.AUTH),
    skipAuth: true,
  });
  return response.data;
};

export const loginSecuredAPI = async (data) => {
  const response = await _api.request({
    url: `/login/secured`,
    method: "POST",
    baseURL: getBaseURL(organization, API_TYPES.AUTH),
    data,
    skipAuth: true,
  });
  return response.data;
};

export const loginAPI = async (organization, user, password) => {
  const response = await _api.request({
    url: `/login/`, //Note: the slash at the end is required as this is the specific api endpoint
    method: "POST",
    baseURL: getBaseURL(organization, API_TYPES.AUTH),
    data: { organization, email: user, password },
    skipAuth: true,
  });
  return response.data;
};

export const refreshTokenAPI = async (organization, refreshToken) => {
  const response = await _api.request({
    url: `/refresh`,
    method: "GET",
    baseURL: getBaseURL(organization, API_TYPES.AUTH),
    headers: {
      ...DEFAULT_HEADERS,
      Authorization: `Bearer ${refreshToken}`,
      organization,
    },
    skipAuth: true,
  });
  return response.data;
};

export const forgotPasswordAPI = async (organization, email) => {
  const response = await _api.request({
    url: `/signup/forgot-password`,
    method: "POST",
    baseURL: getBaseURL(organization, API_TYPES.AUTH),
    data: { organization, email },
    skipAuth: true,
  });
  return response.data;
};

export const setPasswordAPI = async (organization, email, code, password) => {
  const response = await _api.request({
    url: `/signup/set-password`,
    method: "POST",
    baseURL: getBaseURL(organization, API_TYPES.AUTH),
    data: { organization, email, code, password },
    skipAuth: true,
  });
  return response.data;
};

export const getPermissionsAPI = async () => {
  return _apiResponseData({
    endpoint: `/my-permissions`,
    method: "GET",
    apiType: API_TYPES.ACCESS_CONTROL,
  });
};

// V1_LEGACY APIs

export const getProjectsAPI = async (testType) => {
  return _apiResponseData({
    endpoint: `/testexecute/projects`,
    method: "GET",
    params: { testType },
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const getProjectsWithTagsAPI = async (testType) => {
  return _apiResponseData({
    endpoint: `/merged/projects`,
    method: "GET",
    params: { testType },
    apiType: API_TYPES.TAGS,
  });
};

export const getProjectByIdAPI = async (params) => {
  return _apiResponseData({
    endpoint: `/testexecute/projects`,
    method: "GET",
    params,
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const updateProjectAPI = async (projectId, payload = {}) => {
  return _apiResponseData({
    endpoint: `/testexecute/projects`,
    method: "PUT",
    params: { projectId },
    data: payload,
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const deleteProjectAPI = async (projectId) => {
  if (!projectId) {
    throw new Error("Project ID is required for deletion");
  }
  return _apiResponseData({
    endpoint: `/testexecute/projects`,
    method: "DELETE",
    params: { projectId },
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const createProjectAPI = async (payload = {}) => {
  return _apiResponseData({
    endpoint: `/testexecute/projects`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const getAwsSecretsFromTypeAPI = async (projectId, params) => {
  return _apiResponseData({
    endpoint: `/${projectId}`,
    params,
    method: "GET",
    apiType: API_TYPES.AWS_SECRETS,
  });
};

export const getSafeAwsSecretsFromTypeAPI = async (projectId, params) => {
  return _apiResponseData({
    endpoint: `/${projectId}/safe`,
    params,
    method: "GET",
    apiType: API_TYPES.AWS_SECRETS,
  });
};

export const createAwsSecretsFromTypeAPI = async (
  projectId,
  params,
  payload = {}
) => {
  return _apiResponseData({
    endpoint: `/${projectId}`,
    method: "POST",
    data: payload,
    params,
    apiType: API_TYPES.AWS_SECRETS,
  });
};

export const updateAwsSecretsFromTypeAPI = async (
  projectId,
  params,
  payload = {}
) => {
  return _apiResponseData({
    endpoint: `/${projectId}`,
    method: "PUT",
    data: payload,
    params,
    apiType: API_TYPES.AWS_SECRETS,
  });
};

export const deleteAwsSecretsFromTypeAPI = async (projectId, params) => {
  return _apiResponseData({
    endpoint: `/${projectId}`,
    method: "DELETE",
    params,
    apiType: API_TYPES.AWS_SECRETS,
  });
};

export const testProjectConfigsAPI = async (payload) => {
  return _apiResponseData({
    endpoint: `/test-configurations`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.PROJECT_CONFIGS,
  });
};

export const createProjectConfigAPI = async (
  projectId,
  projectName,
  versionControl,
  issueTracker
) => {
  return _apiResponseData({
    method: "POST",
    data: { projectId, projectName, versionControl, issueTracker },
    apiType: API_TYPES.PROJECT_CONFIGS,
  });
};

export const getProjectConfigAPI = async (projectId) => {
  return _apiResponseData({
    endpoint: `/${projectId}`,
    method: "GET",
    apiType: API_TYPES.PROJECT_CONFIGS,
  });
};

export const deleteProjectConfigAPI = async (projectId) => {
  return _apiResponseData({
    endpoint: `/${projectId}`,
    method: "DELETE",
    apiType: API_TYPES.PROJECT_CONFIGS,
  });
};

export const updateVersionControlAPI = async (projectId, payload = {}) => {
  return _apiResponseData({
    endpoint: `/${projectId}/version-control`,
    method: "PATCH",
    data: payload,
    apiType: API_TYPES.PROJECT_CONFIGS,
  });
};

export const updateIssueTrackerAPI = async (projectId, payload = {}) => {
  return _apiResponseData({
    endpoint: `/${projectId}/issue-tracker`,
    method: "PATCH",
    data: payload,
    apiType: API_TYPES.PROJECT_CONFIGS,
  });
};

export const getSamplesMetadataAPI = async () => {
  return _apiResponseData({
    endpoint: "/samples/metadata",
    method: "GET",
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

export const importSamplesAPI = async (projectId, payload = {}) => {
  return _apiResponseData({
    endpoint: `/${projectId}/import-samples`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

export const getDeviceListAPI = async (params) => {
  return _apiResponseData({
    endpoint: `/testexecute/devices`,
    method: "GET",
    params: params,
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const getDeviceTrayListAPI = async (testType) => {
  return _apiResponseData({
    endpoint: `/tray/list`,
    method: "GET",
    params: { testType },
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const deleteDeviceTrayAPI = async (trayid) => {
  return _apiResponseData({
    endpoint: `/tray/delete`,
    method: "DELETE",
    params: { trayid },
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const getFileListAPI = async (params) => {
  return _apiResponse({
    endpoint: `/merged/files`,
    method: "GET",
    params: params,
    apiType: API_TYPES.TAGS,
  });
};

export const postFileListAPI = async (payload = {}) => {
  return _apiResponseData({
    endpoint: `/testexecute/files`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const deleteFileListAPI = async (fileId) => {
  return _apiResponseData({
    endpoint: `/testexecute/files?fileId=${fileId}`,
    method: "DELETE",
    apiType: API_TYPES.V1_LEGACY,
  });
};

const _encodeProjectNameInPayload = async (payload) => {
  // Clone the payload to avoid mutating the original
  let updatedPayload = { ...payload };

  // Extract project array from filtersMap
  let projectArray = updatedPayload.filtersMap.project;

  // Create a new array with both original and encoded elements and update in filtersMap
  updatedPayload.filtersMap.project = [
    ...projectArray,
    ...projectArray.map((item) => encodeURIComponent(item)),
  ];

  return updatedPayload;
};

export const getSessionListAPI = async (payload = {}) => {
  await _encodeProjectNameInPayload(payload);

  return _apiResponseData({
    endpoint: `/analytics/tests`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const getSessionInformationAPI = async (sessionId) => {
  return _apiResponseData({
    endpoint: `/analytics/tests/${sessionId}/info`,
    method: "GET",
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const getSessionFileListAPI = async (testId, type) => {
  return _apiResponseData({
    endpoint: `/testexecute/download`,
    method: "GET",
    params: {
      testId,
      type,
    },
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const getSessionFileContentAPI = async (testId, type, fileName) => {
  return _apiResponseData({
    endpoint: `/testexecute/download`,
    method: "GET",
    params: {
      testId,
      type,
      fileName,
    },
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const getFileDetailsAPI = async (testId, type, fileName) => {
  return _apiResponse({
    endpoint: `/testexecute/download`,
    method: "GET",
    params: {
      testId,
      type,
      fileName,
    },
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const generatePresignedUrlV2API = async (key) => {
  return _apiResponse({
    endpoint: `/testexecute/generate-presigned-url?key=${key}`,
    method: "GET",
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const createTrayAPI = async (trayName, devices) => {
  return _apiResponseData({
    endpoint: `/tray/create`,
    method: "POST",
    data: {
      trayName,
      devices,
    },
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const updateTrayAPI = async (trayid, devices) => {
  return _apiResponseData({
    endpoint: `/tray/update`,
    method: "PUT",
    data: {
      devices,
    },
    params: { trayid },
    apiType: API_TYPES.V1_LEGACY,
  });
};

export const downloadFile = async (fileName, s3PresignedUrl) => {
  try {
    const response = await fetch(s3PresignedUrl);

    if (!response.ok) {
      // todo: fix warning: 'throw' of exception caught locally
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    const blob = await response.blob();

    // Create a temporary download link
    const linkElement = document.createElement("a");
    linkElement.style.display = "none";

    const url = URL.createObjectURL(blob);
    linkElement.href = url;
    linkElement.setAttribute("download", fileName);

    document.body.appendChild(linkElement);
    linkElement.click();

    // Clean up the temporary URL
    URL.revokeObjectURL(url);
    document.body.removeChild(linkElement);
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

export const fetchFileContentAPI = async (file) => {
  try {
    const response = await axios.get(file?.url);
    if (_isString(response?.data)) {
      return response?.data;
    }
    return response?.data ? JSON.stringify(response?.data, null, 2) : undefined;
  } catch (err) {
    console.log("Error fetching data:", err);
  }
  return undefined;
};

export const getFileAPI = async (filePath, projectId, params) => {
  return _apiResponseData({
    endpoint: `/${projectId}/${filePath}`,
    method: "GET",
    params,
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

// CSV APIs

export const getCsvFileAPI = async (filePath, projectId) => {
  return _apiResponseData({
    endpoint: `/${projectId}/${filePath}`,
    method: "GET",
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
    extraHeaders: { "Content-Type": "text/csv" },
  });
};

// NON-JSON APIs

export const uploadMultiPartFormDataAPI = async (projectId, payload = {}) => {
  return _apiResponseData({
    endpoint: `/${projectId}/upload-file`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
    extraHeaders: { "Content-Type": "multipart/form-data" },
  });
};

export const uploadMultipleFilesAPI = async (projectId, payload = {}) => {
  return _apiResponseData({
    endpoint: `/${projectId}/upload-folder`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
    extraHeaders: { "Content-Type": "multipart/form-data" },
  });
};

export const getImageAPI = async (filePath, projectId) => {
  return _apiResponseData({
    endpoint: `/${projectId}/${filePath}`,
    method: "GET",
    apiType: API_TYPES.FILE_CACHE,
    responseType: "blob",
  });
};

export const createFolderAPI = async (folderPath, projectId) => {
  return _apiResponseData({
    endpoint: `/projects/${projectId}/create-folder`,
    method: "POST",
    data: {
      path: folderPath,
    },
  });
};

export const getSourceTreeAPI = async (projectId, params) => {
  return _apiResponseData({
    endpoint: `${projectId}/directory-structures`,
    method: "GET",
    params,
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

export const generateSuiteAPI = async (payload = {}) => {
  return _apiResponse({
    endpoint: `/projects/myproj/generate-suite`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.TCM_V2,
  });
};

export const generatePytestAPI = async (payload) => {
  return _apiResponse({
    endpoint: `/optics-pytest`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.CODEGEN,
  });
};

export const generateWebCodeAPI = async (payload) => {
  return _apiResponse({
    endpoint: `/web-automation`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.CODEGEN,
  });
};

export const fetchTestModulesAPI = async (payload, projectId, params) => {
  return _apiResponseData({
    endpoint: `/merged/test-modules/${projectId}`,
    method: "POST",
    data: payload,
    params,
    apiType: API_TYPES.TAGS,
  });
};

export const fetchTestCasesAPI = async (payload, projectId, params) => {
  return _apiResponseData({
    endpoint: `/merged/test-cases/${projectId}`,
    method: "POST",
    data: payload,
    params,
    apiType: API_TYPES.TAGS,
  });
};

export const fetchTestDataAPI = async (payload, projectId, params) => {
  return _apiResponseData({
    endpoint: `/merged/test-data/${projectId}`,
    method: "POST",
    data: payload,
    params,
    apiType: API_TYPES.TAGS,
  });
};

export const createGitTagAPI = async (payload, projectId) => {
  return _apiResponseData({
    endpoint: `/${projectId}/git-tags`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

export const listGitTagsAPI = async (projectId) => {
  return _apiResponseData({
    endpoint: `/${projectId}/git-tags`,
    method: "GET",
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

export const executeJobAPI = async (jobRunnerPayload) => {
  return _apiResponseData({
    endpoint: `/job`,
    method: "POST",
    apiType: API_TYPES.JOB_RUNNER,
    data: jobRunnerPayload,
  });
};

export const getJobInfoAPI = async (jobId, params) => {
  return _apiResponseData({
    endpoint: `/job/${jobId}`,
    method: "GET",
    params: params,
    apiType: API_TYPES.JOB_RUNNER,
  });
};

export const getJobTestIdsAPI = async (jobId) => {
  const params = { showTests: true };
  return _apiResponseData({
    endpoint: `/job/${jobId}`,
    method: "GET",
    params: params,
    apiType: API_TYPES.JOB_RUNNER,
  });
};

export const abortJobAPI = async (jobId) => {
  return _apiResponseData({
    endpoint: `/abort/${jobId}`,
    method: "POST",
    apiType: API_TYPES.JOB_RUNNER,
  });
};

export const generateJobSummaryReportAPI = async (jobId) => {
  return _apiResponseData({
    endpoint: `/job-summary`,
    method: "POST",
    data: { jobId },
    apiType: API_TYPES.REPORTS,
  });
};

export const generateJobReportAPI = async (jobId, template) => {
  return _apiResponseData({
    endpoint: `/job-report`,
    method: "POST",
    data: { jobId, ...(template && { template }) },
    apiType: API_TYPES.REPORTS,
  });
};

export const generateCompareReportAPI = async (jobId1, jobId2) => {
  return _apiResponseData({
    endpoint: `/job-compare`,
    method: "POST",
    data: { jobId1, jobId2 },
    apiType: API_TYPES.REPORTS,
  });
};

export const getSessionScreenshotsAPI = async (testId, params = {}) => {
  return _apiResponseData({
    endpoint: `/${testId}`,
    method: "GET",
    params,
    apiType: API_TYPES.SESSION_SCREENSHOTS,
  });
};

export const getSessionScreenshotCountsAPI = async (testId, params = {}) => {
  return _apiResponseData({
    endpoint: `/${testId}/counts`,
    method: "GET",
    params,
    apiType: API_TYPES.SESSION_SCREENSHOTS,
  });
};

export const batchGetSessionScreenshotsAPI = async (payload = {}) => {
  return _apiResponseData({
    endpoint: `/batch`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.SESSION_SCREENSHOTS,
  });
};

export const deleteJobAPI = async (jobId) => {
  return _apiResponseData({
    endpoint: `/job/${jobId}`,
    method: "DELETE",
    apiType: API_TYPES.JOB_RUNNER,
  });
};

export const getTestCaseReportAPI = async (testId) => {
  return _apiResponseData({
    endpoint: `/export/file/`,
    method: "GET",
    apiType: API_TYPES.V1_LEGACY,
    params: {
      fileName: `execution-output-files/${testId}/test_report.json`,
    },
  });
};

export const getAllChangeRequestsAPI = async (
  projectId,
  status = null,
  page,
  pagelen,
  sortOrder
) => {
  const params = {
    ...(status && { status }),
    ...(page && { page }),
    ...(pagelen && { pagelen }),
    ...(sortOrder ? { sortOrder } : {}),
  };

  return _apiResponseData({
    endpoint: `/${projectId}/change-requests/`,
    method: "GET",
    params,
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

export const getChangeRequestFileDiff = async (projectId, prId) => {
  return _apiResponseData({
    endpoint: `/${projectId}/change-requests/${prId}/diff`,
    method: "GET",
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

export const createChangeRequest = async (projectId, payload = {}) => {
  const { files } = payload;

  const filesWithEncodedData = files.map((file) => ({
    filePath: file.filePath,
    encodedData: encodeContent(file.data),
  }));

  payload["files"] = filesWithEncodedData;

  return _apiResponseData({
    endpoint: `/${projectId}/change-requests`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

export const updateChangeRequest = async (projectId, prId, payload = {}) => {
  return _apiResponseData({
    endpoint: `/${projectId}/change-requests/${prId}`,
    method: "PATCH",
    data: payload,
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

export const changeRequestAction = async (projectId, prId, payload = {}) => {
  return _apiResponseData({
    endpoint: `/${projectId}/change-requests/${prId}/action`,
    method: "PATCH",
    data: payload,
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

export const mergeChangeRequest = async (projectId, prId) => {
  return _apiResponseData({
    endpoint: `/${projectId}/change-requests/${prId}/merge`,
    method: "PATCH",
    apiType: API_TYPES.VERSION_CONTROL_SYSTEM,
  });
};

export const createJiraIssueAPI = async (projectId, payload = {}) => {
  return _apiResponseData({
    endpoint: `/${projectId}/create-issue`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.JIRA_ISSUE,
  });
};

export const createTagAPI = async (payload = {}) => {
  return _apiResponseData({
    endpoint: `/`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.TAGS,
  });
};

export const fetchTagsAPI = async () => {
  return _apiResponseData({
    endpoint: `/`,
    method: "GET",
    apiType: API_TYPES.TAGS,
  });
};

export const assignTagAPI = async (payload = {}) => {
  return _apiResponseData({
    endpoint: `/assign`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.TAGS,
  });
};

export const unAssignTagAPI = async (payload = {}) => {
  return _apiResponseData({
    endpoint: `/remove`,
    method: "DELETE",
    data: payload,
    apiType: API_TYPES.TAGS,
  });
};

// OPTICS APIs using _apiResponseData

export const startOpticsSession = async (deviceId, payload) => {
  return _apiResponseData({
    endpoint: `${deviceId}/v1/sessions/start`,
    method: "POST",
    data: payload,
    apiType: API_TYPES.OPTICS,
    timeout: 120000, // appium server takes time to start because of network hopping
    // Note: the actual timeout is observed as 1 min. This may be due to a server side setting
  });
};

export const stopOpticsSession = async (deviceId, sessionId) => {
  return _apiResponseData({
    endpoint: `${deviceId}/v1/sessions/${sessionId}/stop`,
    method: "DELETE",
    apiType: API_TYPES.OPTICS,
  });
};

export const executeOpticsKeyword = async (
  deviceId,
  sessionId,
  keyword,
  params = []
) => {
  return _apiResponseData({
    endpoint: `${deviceId}/v1/sessions/${sessionId}/action`,
    method: "POST",
    data: {
      mode: "keyword",
      keyword,
      params,
    },
    apiType: API_TYPES.OPTICS,
  });
};

export const getOpticsScreenshot = async (deviceId, sessionId) => {
  return _apiResponseData({
    endpoint: `${deviceId}/session/${sessionId}/screenshot`,
    method: "GET",
    apiType: API_TYPES.OPTICS,
  });
};

export const getOpticsElements = async (deviceId, sessionId) => {
  return _apiResponseData({
    endpoint: `${deviceId}/session/${sessionId}/elements`,
    method: "GET",
    apiType: API_TYPES.OPTICS,
  });
};

export const getOpticsPageSource = async (deviceId, sessionId) => {
  return _apiResponseData({
    endpoint: `${deviceId}/session/${sessionId}/source`,
    method: "GET",
    apiType: API_TYPES.OPTICS,
  });
};

export const getOpticsScreenElements = async (deviceId, sessionId) => {
  return _apiResponseData({
    endpoint: `${deviceId}/session/${sessionId}/screen_elements`,
    method: "GET",
    apiType: API_TYPES.OPTICS,
  });
};

export const subscribeToOpticsEvents = (sessionId, onEvent) => {
  const org = localStorage.getItem("organization");
  const baseUrl = getBaseURL(org, API_TYPES.OPTICS);
  const eventSource = new EventSource(
    `${baseUrl}/v1/sessions/${sessionId}/events`
  );

  eventSource.onmessage = (event) => {
    const parsed = JSON.parse(event.data);
    onEvent(parsed);
  };

  eventSource.onerror = (err) => {
    console.error("SSE error:", err);
    eventSource.close();
  };

  return () => eventSource.close(); // unsubscribe function
};

export const forceStopDeviceAndFreeSlots = async (deviceId) => {
  return _apiResponseData({
    endpoint: `${deviceId}/free`,
    method: "PATCH",
    apiType: API_TYPES.DEVICES,
  });
};

export const fetchBrowsersAPI = async () => {
  return _apiResponseData({
    endpoint: `/`,
    method: "GET",
    apiType: API_TYPES.BROWSERS,
  });
};

//todo: Change endpoint to /playwright
export const launchWebAutomationJobAPI = async (payload) => {
  return _apiResponseData({
    endpoint: "/playwrite",
    method: "POST",
    data: payload,
  });
};

export const fetchNestedTestCasesAPI = async (payload, projectId, params) => {
  return _apiResponseData({
    endpoint: `/test-cases/${projectId}`,
    method: "POST",
    data: payload,
    params,
    apiType: API_TYPES.TEST_STUDIO,
  });
};

export const fetchFilteredJobSettingsAPI = async (payload, params) => {
  return _apiResponseData({
    endpoint: `/filter`,
    method: "POST",
    data: payload,
    params,
    apiType: API_TYPES.JOB_SETTINGS,
  });
};

export const updateJobSettingAPI = async (id, payload, params) => {
  return _apiResponseData({
    endpoint: `/${id}`,
    method: "PUT",
    data: payload,
    params,
    apiType: API_TYPES.JOB_SETTINGS,
  });
};

export const createJobSettingAPI = async (payload, params) => {
  return _apiResponseData({
    endpoint: `/`,
    method: "POST",
    data: payload,
    params,
    apiType: API_TYPES.JOB_SETTINGS,
  });
};

export const deleteJobSettingAPI = async (id) => {
  return _apiResponseData({
    endpoint: `/${id}`,
    method: "DELETE",
    apiType: API_TYPES.JOB_SETTINGS,
  });
};

export const jobSubmitAPI = async (payload, params) => {
  return _apiResponseData({
    endpoint: `/`,
    method: "POST",
    data: payload,
    params,
    apiType: API_TYPES.JOB_SUBMIT,
  });
};
